import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import Recaptcha from '@components/recaptcha'
import MarketingOptIn from '@components/marketing-opt-in'
import { gtmEvent } from '@lib/gtm-utils'
import Link from '@material-ui/core/Link'
import EmailFieldWithSuggestions from '@components/form-utils/email-field-with-suggestions'
import { useStyles } from './styles'

const CREATE_WAITLIST_ENTRY = gql`
  mutation CreateWaitlistEntry($input: WaitlistEntryInput!) {
    createGroupEventWaitlistEntry(input: $input) {
      success
    }
  }
`

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .max(50)
    .required('First name is required')
    .label('First name'),
  lastName: Yup.string()
    .trim()
    .max(100)
    .required('Last name is required')
    .label('Last name'),
  email: Yup.string()
    .email('Please enter a valid email')
    .max(100)
    .required('Email is required')
    .label('Email'),
  groupEventId: Yup.string().required(),
})

const JoinWaitlistForm = ({ groupEventId, setIsFormSubmitted, onSuccess }) => {
  const styles = useStyles()
  const [isFormSubmitSuccess, setIsFormSubmitSuccess] = useState(null)
  const [recaptchaToken, setRecaptchaToken] = useState(null)
  const [marketingOptInChecked, setMarketingOptInChecked] = useState(false)

  const {
    handleSubmit,
    register,
    control,
    errors,
    formState: { isSubmitting, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    shouldFocusError: true,
  })
  const [createWaitlistEntry] = useMutation(CREATE_WAITLIST_ENTRY, {
    context: { clientName: 'coach-tools', recaptchaToken },
    onCompleted: (data) => {
      setIsFormSubmitSuccess(data.createGroupEventWaitlistEntry.success)
      if (data.createGroupEventWaitlistEntry.success) {
        if (marketingOptInChecked) {
          gtmEvent({
            event: 'marketing-opt-in',
            source: 'waitlist_joined',
          })
        }
        if (onSuccess) {
          onSuccess()
        }
        setIsFormSubmitted(true)
      }
    },
    onError: () => {
      setIsFormSubmitSuccess(false)
    },
  })

  const canSubmit = isValid && !isSubmitting && recaptchaToken

  const onSubmit = (values) => {
    const marketingOptInAt = marketingOptInChecked
      ? new Date().toISOString()
      : null
    setIsFormSubmitSuccess(null)
    window.dataLayer.push({
      event: 'waitlistSubmit',
      waitlistEventId: groupEventId,
    })

    return createWaitlistEntry({
      variables: {
        input: {
          groupEventId,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          marketingOptInAt,
        },
      },
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box display="flex" mt={2.5} mb={1.5} width="100%">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firstName"
                label="First name"
                variant="outlined"
                inputRef={register}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
                style={{ marginRight: '12px' }}
                className={styles.formInput}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastName"
                label="Last name"
                variant="outlined"
                inputRef={register}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
                className={styles.formInput}
              />
            </Grid>
          </Grid>
        </Box>
        <Box width="100%" mb={4}>
          <EmailFieldWithSuggestions
            fieldName="email"
            label="Email Address"
            variant="outlined"
            control={control}
            errors={errors}
            className={styles.formInput}
            fullWidth
          />
        </Box>
        {isFormSubmitSuccess === false && (
          <Typography variant="caption" color="error" gutterBottom>
            An error has occurred. Please try again later.
          </Typography>
        )}
        <input type="hidden" name="groupEventId" value={groupEventId} ref={register} />
        <MarketingOptIn
          checked={marketingOptInChecked}
          onChange={setMarketingOptInChecked}
          useMarketingOptIn="standard"
        />
        <Box mb={1} textAlign="left">
          <Typography variant="body2" className={styles.proceedingText}>
            By proceeding, you agree to PGA of America’s{' '}
            <Link
              className={styles.marketingLink}
              href="https://www.pga.com/pga-of-america/privacy-policy"
              target="_blank"
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              className={styles.marketingLink}
              href="https://www.pga.com/pga-of-america/terms-of-service"
              target="_blank"
            >
              Terms of Service
            </Link>
            .
          </Typography>
          <Recaptcha setToken={setRecaptchaToken} />
        </Box>
      </Box>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!canSubmit}
      >
        Submit
      </Button>
    </form>
  )
}

JoinWaitlistForm.propTypes = {
  groupEventId: PropTypes.string.isRequired,
  setIsFormSubmitted: PropTypes.func.isRequired,
}

export default JoinWaitlistForm
